import {
  Boxes,
  Building,
  CloudDownload,
  CloudUpload,
  Database,
  FileSpreadsheet,
  Flag,
  Flame,
  Gauge,
  Leaf,
  Lightbulb,
  Settings,
  ShieldCheck,
  BadgeDollarSign,
  UserPlus,
  Zap,
} from "lucide-react";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import type { ModuleDefinition } from "@/lib/module-registry";

/**
 * Admin — platform and tenant configuration.
 *
 * The config half of the former "Global module". Global mixed two unlike things
 * in one destination: shared *records* (clients, accounts, suppliers,
 * utilities) and *configuration* (users, permissions, flags, catalogs). Every
 * comparable product keeps those apart — Salesforce has Setup, HubSpot has
 * Settings, Dynamics has the Power Platform admin center — because they're
 * used at different times by different people. Records are day-to-day work
 * and live in the `RecordBar` (Energy Advisor and Operations); config is
 * occasional and lives here.
 *
 * Two entries were mis-filed under Global's "Configuration" section
 * alongside real records — Reference Data and Product Catalog. Both are
 * config and move here, which is why the split doesn't follow Global's old
 * `data` / `access` section boundary.
 *
 * **Paths deliberately match `main`'s** (`/users`, `/segments`,
 * `/settings/*`) rather than living under an `/admin/*` prefix. This module is
 * the flag-on presentation of pages `main` reaches through its Access and
 * Settings sidebar groups; keeping the urls identical means the PowerUp module
 * shell can be feature-flagged without any route differing between flag
 * states — only which chrome and which nav surfaces them changes. An
 * `/admin/*` prefix would have read better but forced dual registration and
 * flag-aware links for every one of these pages.
 */

const CapabilityProposalsPage = lazy(
  () => import("@/pages/settings/CapabilityProposalsPage"),
);
const FeatureFlagsPage = lazy(() =>
  import("@/pages/settings/FeatureFlagsPage").then((m) => ({
    default: m.FeatureFlagsPage,
  })),
);
const PipelineReportPage = lazy(() =>
  import("@/pages/settings/PipelineReportPage").then((m) => ({
    default: m.PipelineReportPage,
  })),
);
const PermissionSetsPage = lazy(() =>
  import("@/pages/settings/PermissionSetsPage").then((m) => ({
    default: m.PermissionSetsPage,
  })),
);
const PriceAdjustmentsPage = lazy(() =>
  import("@/pages/settings/PriceAdjustmentsPage").then((m) => ({
    default: m.PriceAdjustmentsPage,
  })),
);
const EnbridgeRatesPage = lazy(() =>
  import("@/pages/settings/EnbridgeRatesPage").then((m) => ({
    default: m.EnbridgeRatesPage,
  })),
);
const PjmCapacityPage = lazy(() =>
  import("@/pages/settings/PjmCapacityPage").then((m) => ({
    default: m.PjmCapacityPage,
  })),
);
const RecCertificatesPage = lazy(() =>
  import("@/pages/settings/RecCertificatesPage").then((m) => ({
    default: m.RecCertificatesPage,
  })),
);
const PjmTransmissionPage = lazy(() =>
  import("@/pages/settings/PjmTransmissionPage").then((m) => ({
    default: m.PjmTransmissionPage,
  })),
);
const ProductCatalogPage = lazy(() =>
  import("@/pages/settings/ProductCatalogPage").then((m) => ({
    default: m.ProductCatalogPage,
  })),
);
const ReferenceDataPage = lazy(() =>
  import("@/pages/settings/ReferenceDataPage").then((m) => ({
    default: m.ReferenceDataPage,
  })),
);
const HubspotSyncPage = lazy(() =>
  import("@/pages/settings/HubspotSyncPage").then((m) => ({
    default: m.HubspotSyncPage,
  })),
);
const SalesforceImportPage = lazy(() =>
  import("@/pages/settings/SalesforceImportPage").then((m) => ({
    default: m.SalesforceImportPage,
  })),
);
const SegmentsPage = lazy(() =>
  import("@/pages/segments/SegmentsPage").then((m) => ({
    default: m.SegmentsPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/pages/users/UsersPage").then((m) => ({ default: m.UsersPage })),
);

// Tab routes stay in their own co-located module — `ProductCatalogPage`
// imports them, and this file imports the page, so inlining them here would
// close an import cycle.
import { PRODUCT_CATALOG_TAB_ROUTES } from "@/pages/settings/product-catalog/product-catalog-tabs";

export const ADMIN_MODULE: ModuleDefinition = {
  key: "admin",
  label: "Admin",
  icon: Settings,
  description: "Users, permissions, feature flags, and shared catalogs.",
  // Users, not the alphabetically-first item (Feature Flags): Feature Flags
  // is super-admin-only, so landing a tenant admin there would bounce them
  // straight back out. Users is the broadest-gated Admin page and the one
  // people actually come here for.
  entryPath: "/users",
  requiredPermission: "admin.access",
  // A Pilot super admin signed in to a partner tenant manages that tenant's
  // feature flags and permission sets from here (AB#8687), whatever their
  // role in it.
  openToSuperAdmins: true,

  // No Records section, and no record bar either (`recordBar` unset,
  // AB#8790): Admin is config, not record work. Shared records live in the
  // horizontal `RecordBar` in Energy Advisor and Operations. See
  // `components/layout/RecordBar.tsx`.
  sections: [
    {
      // Who may do what. Items A–Z by label, then Pipeline Report, which
      // the business places under Users rather than in letter order.
      key: "admin-access",
      label: "Access",
      preserveOrder: true,
      routes: [
        {
          path: "/settings/feature-flags",
          label: "Feature Flags",
          icon: Flag,
          status: "live",
          element: <FeatureFlagsPage />,
          requiresSuperAdmin: true,
        },
        {
          path: "/settings/permission-sets",
          label: "Permission Sets",
          icon: ShieldCheck,
          status: "live",
          element: <PermissionSetsPage />,
          requiresSuperAdmin: true,
        },
        {
          // A segment is a business-unit grouping users belong to, so it
          // files under Access with Users rather than with the catalogs.
          path: "/segments",
          label: "Segments",
          icon: Building,
          status: "live",
          element: <SegmentsPage />,
          requiredPermission: "segments.manage",
        },
        {
          path: "/users",
          label: "Users",
          icon: UserPlus,
          status: "live",
          element: <UsersPage />,
          requiredPermission: "users.view",
        },
        {
          // The Energy Advisor dashboard's Pipeline Report, for users whose
          // permission set has no Energy Advisor access. Gated like the
          // report's endpoint: Reports access here, Deals export there.
          path: "/pipeline-report",
          label: "Pipeline Report",
          icon: FileSpreadsheet,
          status: "live",
          element: <PipelineReportPage />,
          requiredPermission: "reports.view",
        },
      ],
    },
    {
      // Market and rate inputs a super-admin maintains and pricing reads:
      // ISO charges, tariffs, REC symbols, and the adders on top of Platts
      // assessments. Split out of Catalogs, which had become three kinds of
      // thing under one name — these, the product's own vocabularies, and
      // the capability backlog. Price Adjustments moved here from Access,
      // where it never fit: it's pricing data, not who-may-do-what.
      //
      // Enbridge Rates is strictly a tariff rather than market data, but it
      // files with the PJM datasets for the same reason they're here: a
      // cross-tenant input the SES Calculators read (AB#8344), maintained in
      // Admin rather than under Operations' own settings gear.
      //
      // Items A–Z by label.
      key: "admin-market-data",
      label: "Market Data",
      routes: [
        {
          path: "/settings/enbridge-rates",
          label: "Enbridge Rates",
          icon: Flame,
          status: "live",
          element: <EnbridgeRatesPage />,
          requiresSuperAdmin: true,
        },
        {
          path: "/settings/pjm-capacity",
          label: "PJM Capacity",
          icon: Gauge,
          status: "live",
          element: <PjmCapacityPage />,
          requiresSuperAdmin: true,
        },
        {
          path: "/settings/pjm-transmission",
          label: "PJM Transmission",
          icon: Zap,
          status: "live",
          element: <PjmTransmissionPage />,
          requiresSuperAdmin: true,
        },
        {
          path: "/settings/price-adjustments",
          label: "Price Adjustments",
          icon: BadgeDollarSign,
          status: "live",
          element: <PriceAdjustmentsPage />,
          requiresSuperAdmin: true,
        },
        {
          // The naming authority for `market.lake_rec_symbols`. Market
          // Intelligence cross-links here rather than carrying its own copy
          // — the same arrangement as PJM Capacity above: a second editable
          // copy is how the numbers drift.
          path: "/settings/rec-certificates",
          label: "REC Certificates",
          icon: Leaf,
          status: "live",
          element: <RecCertificatesPage />,
          requiresSuperAdmin: true,
        },
      ],
    },
    {
      // The product's own shared vocabularies. Items A–Z by label.
      key: "admin-catalogs",
      label: "Catalogs",
      routes: [
        {
          // Moved out of the Energy Advisor module's settings gear, where it
          // had been filed by elimination. It's the cross-tenant backlog of
          // agent-authored capability gaps — `capability_proposals.rs` says
          // "cross-tenant because the backlog is platform-wide" — so it's
          // platform config, not advisor config. Files under Catalogs as the
          // closest fit: a platform-wide dataset a super-admin reads, like
          // Reference Data beside it. Not a catalog strictly; if Maxwell gains
          // more admin pages, they earn their own section and this joins them.
          path: "/settings/capability-proposals",
          label: "Capability Proposals",
          icon: Lightbulb,
          status: "live",
          element: <CapabilityProposalsPage />,
          requiresSuperAdmin: true,
        },
        {
          path: "/settings/product-catalog",
          label: "Product Catalog",
          icon: Boxes,
          status: "live",
          element: <ProductCatalogPage />,
          requiresSuperAdmin: true,
          children: [...PRODUCT_CATALOG_TAB_ROUTES],
        },
        {
          path: "/settings/reference-data",
          label: "Reference Data",
          icon: Database,
          status: "live",
          element: <ReferenceDataPage />,
          requiresSuperAdmin: true,
        },
      ],
    },
    {
      // What this tenant pushes to a system outside PowerUp. Its own section:
      // it configures nothing and it loads nothing in, so neither the
      // sections above nor Imports below describes it.
      //
      // Items A–Z by label.
      key: "admin-integrations",
      label: "Integrations",
      routes: [
        {
          // Super-admin only: the review list shows companies from Pilot
          // Energy's HubSpot portal, which a partner's own admins must not
          // browse. A super admin signed in to the partner tenant runs it.
          path: "/settings/hubspot-sync",
          label: "HubSpot Sync",
          icon: CloudUpload,
          status: "live",
          element: <HubspotSyncPage />,
          requiresSuperAdmin: true,
        },
      ],
    },
    {
      // What a data migration loaded into this tenant. Its own section: it
      // configures nothing, so none of the sections above describes it.
      //
      // Items A–Z by label.
      key: "admin-imports",
      label: "Imports",
      routes: [
        {
          // A tenant admin's page, not super-admin: the people checking that
          // their own migration landed are the tenant's admins.
          path: "/settings/salesforce-import",
          label: "Salesforce Import",
          icon: CloudDownload,
          status: "live",
          element: <SalesforceImportPage />,
        },
      ],
    },
  ],

  hiddenRoutes: [
    {
      // Entering Admin lands on its first real page — there's no Admin
      // overview worth designing, same call Global made.
      path: "/admin",
      label: "Admin",
      status: "live",
      element: <Navigate to="/users" replace />,
    },
  ],
};
